import { getSupabaseBrowserClient, type User, type Session, type SavedDeck, type SavedDeckSummary } from './supabase';
import {
  persistCardImages,
  deleteDecksImages,
  uploadOriginalFile,
  deleteOriginalFile,
  hydrateCardStorageUrls,
  normalizeCardStorageReferences,
  resolveStorageReference,
} from './imageStorage';
import type { FlashCard } from '../app/lib/api';
import { randomUUID } from './uuid';

// Sign in with Google OAuth
export async function signInWithGoogle(redirectTo?: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) {
    console.error('[Auth] Supabase client not available');
    return;
  }

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectTo || `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    console.error('[Auth] Sign in error:', error.message);
    throw error;
  }
}

// Sign out
export async function signOut(): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return;

  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('[Auth] Sign out error:', error.message);
    throw error;
  }
}

// Get current session
export async function getSession(): Promise<Session | null> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return null;

  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('[Auth] Get session error:', error.message);
    return null;
  }

  return session;
}

// Get current user
export async function getUser(): Promise<User | null> {
  const session = await getSession();
  return session?.user ?? null;
}

// Link device_id to user account after sign-in
// This is optional - fails silently if the user_id column doesn't exist yet
export async function linkDeviceToUser(deviceId: string, userId: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) return;

  try {
    const { error } = await supabase
      .from('anonymous_users')
      .update({ user_id: userId })
      .eq('device_id', deviceId);

    // Silently ignore errors (column might not exist if migration not run)
    if (error && !error.message.includes('user_id')) {
      console.error('[Auth] Failed to link device to user:', error.message);
    }
  } catch {
    // Silently ignore - migration may not have been run yet
  }
}

// Save deck to cloud library
export async function saveDeckToLibrary(
  userId: string,
  fileName: string,
  cards: unknown[],
  sessionId?: string | null,
  originalFile?: File | null,
): Promise<SavedDeck> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) throw new Error('Cloud library is not configured');

  // Generate a deck ID upfront so we can use it for image storage
  const deckId = randomUUID();

  // Persist images to Supabase Storage (replaces local filenames with URLs)
  let persistedCards = cards as FlashCard[];
  const hasImages = (cards as FlashCard[]).some(
    c => c.type === 'image_occlusion' || c.image
  );

  let originalFileUrl: string | null = null;
  try {
    if (hasImages) {
      persistedCards = await persistCardImages(userId, deckId, cards as FlashCard[], sessionId);
    }
    if (originalFile) {
      originalFileUrl = await uploadOriginalFile(userId, deckId, originalFile);
    }
  } catch (error) {
    await Promise.allSettled([
      deleteDecksImages(userId, deckId),
      deleteOriginalFile(userId, deckId),
    ]);
    throw error;
  }

  const insertData: Record<string, unknown> = {
    id: deckId,
    user_id: userId,
    file_name: fileName,
    cards: persistedCards,
    card_count: persistedCards.length,
  };

  // Store sessionId as fallback for any images that failed to persist
  if (sessionId) {
    insertData.session_id = sessionId;
  }

  // Store original file URL if uploaded
  if (originalFileUrl) {
    insertData.original_file_url = originalFileUrl;
  }

  const { data, error } = await supabase
    .from('saved_decks')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('[Auth] Failed to save deck:', error.message);
    // Clean up any uploaded files if deck save failed
    if (hasImages) {
      await deleteDecksImages(userId, deckId).catch(() => {});
    }
    if (originalFileUrl) {
      await deleteOriginalFile(userId, deckId).catch(() => {});
    }
    throw error;
  }

  return data;
}

// Get all saved decks for user
export async function getSavedDecks(userId: string): Promise<SavedDeckSummary[]> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) throw new Error('Cloud library is not configured');

  const { data, error } = await supabase
    .from('saved_decks')
    .select('id,user_id,file_name,card_count,session_id,original_file_url,created_at,updated_at,folder_id')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Delete a saved deck
export async function deleteSavedDeck(deckId: string, userId?: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) throw new Error('Cloud library is not configured');

  // Delete the protected database row first. Storage is cleaned only after the
  // authoritative delete succeeds, preventing failed DB deletes from losing files.
  const { error } = await supabase
    .from('saved_decks')
    .delete()
    .eq('id', deckId)
    .select('id')
    .single();

  if (error) {
    console.error('[Auth] Failed to delete deck:', error.message);
    throw error;
  }

  if (userId) {
    const cleanup = await Promise.allSettled([
      deleteDecksImages(userId, deckId),
      deleteOriginalFile(userId, deckId),
    ]);
    cleanup.forEach(result => {
      if (result.status === 'rejected') console.error('[Auth] Storage cleanup failed:', result.reason);
    });
  }
}

// Get a single saved deck by ID
export async function getSavedDeck(deckId: string): Promise<SavedDeck | null> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) throw new Error('Cloud library is not configured');

  const { data, error } = await supabase
    .from('saved_decks')
    .select('*')
    .eq('id', deckId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    ...data,
    cards: await hydrateCardStorageUrls(data.cards as FlashCard[]),
    original_file_url: await resolveStorageReference(data.original_file_url),
  };
}

// Update an existing saved deck
export async function updateSavedDeck(
  deckId: string,
  cards: unknown[],
  fileName?: string,
): Promise<SavedDeck> {
  const supabase = getSupabaseBrowserClient();
  if (!supabase) throw new Error('Cloud library is not configured');

  const updates: Record<string, unknown> = {
    cards: normalizeCardStorageReferences(cards as FlashCard[]),
    card_count: cards.length,
  };

  if (fileName) {
    updates.file_name = fileName;
  }

  const { data, error } = await supabase
    .from('saved_decks')
    .update(updates)
    .eq('id', deckId)
    .select()
    .single();

  if (error) {
    console.error('[Auth] Failed to update deck:', error.message);
    throw error;
  }

  return data;
}
